/**
 * Hook pour le prévisionnel de trésorerie
 * Récupère et calcule tous les flux financiers sur 3 mois glissants
 * Migré vers React Query (useQuery + useMutation)
 * @module hooks/useTresorerie
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateTresorerie } from '@/lib/queryInvalidation';
import { addMonths, startOfMonth, endOfMonth, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type {
  FluxTresorerie,
  MoisTresorerie,
  PrevisionTresorerie,
  ParametresTresorerie,
  FluxSQL,
  SalairesMoisSQL,
  CategorieEncaissement,
  CategorieDecaissement,
} from '@/types/tresorerie';
import type { ChargeFix } from '@/types/charges-fixes.types';

// =============================================================================
// Types de retour du hook
// =============================================================================

export interface UseTresorerieReturn {
  /** Prévision de trésorerie complète */
  prevision: PrevisionTresorerie | null;
  /** Paramètres utilisateur */
  params: ParametresTresorerie | null;
  /** Chargement en cours */
  isLoading: boolean;
  /** Erreur éventuelle */
  error: string | null;
  /** Mettre à jour le solde manuel */
  updateSoldeManuel: (montant: number | null) => Promise<void>;
  /** Rafraîchir les données */
  refetch: () => Promise<void>;
}

// =============================================================================
// Utilitaire charges fixes
// =============================================================================

/**
 * Détermine si une charge fixe tombe dans un mois donné selon sa fréquence
 */
function chargeApplicableCeMois(charge: ChargeFix, moisCible: string): boolean {
  if (charge.frequence === 'mensuel') return true;

  const createdAt = new Date(charge.created_at);
  const moisCreation = createdAt.getMonth();
  const [, moisStr] = moisCible.split('-');
  const moisCibleIndex = parseInt(moisStr, 10) - 1;

  if (charge.frequence === 'trimestriel') {
    return ((moisCibleIndex - moisCreation + 12) % 12) % 3 === 0;
  }

  if (charge.frequence === 'annuel') {
    return moisCibleIndex === moisCreation;
  }

  return false;
}

// =============================================================================
// Fonction de chargement (extraite pour la queryFn)
// =============================================================================

async function loadPrevisionData(entrepriseId: string): Promise<{
  prevision: PrevisionTresorerie;
  params: ParametresTresorerie;
}> {
  // Charger les paramètres
  const { data: paramsData, error: paramsError } = await supabase
    .from('entreprises')
    .select('params_tresorerie')
    .eq('id', entrepriseId)
    .single();

  if (paramsError) throw paramsError;

  const params: ParametresTresorerie = paramsData?.params_tresorerie || {
    solde_initial_manuel: null,
    utiliser_solde_manuel: false,
  };

  // Dates de la période (3 mois glissants)
  const dateDebut = startOfMonth(new Date());
  const dateFin = endOfMonth(addMonths(new Date(), 2));
  const dateDebutStr = format(dateDebut, 'yyyy-MM-dd');
  const dateFinStr = format(dateFin, 'yyyy-MM-dd');

  // 1. Récupérer le solde actuel (TTC + HT)
  let soldeActuel: number;
  let soldeActuelHt: number;
  let soldeSource: 'manuel' | 'calcule';

  if (params?.utiliser_solde_manuel && params.solde_initial_manuel !== null) {
    soldeActuel = params.solde_initial_manuel;
    soldeActuelHt = Math.round(params.solde_initial_manuel / 1.2);
    soldeSource = 'manuel';
  } else {
    const { data: soldeData, error: soldeError } = await supabase.rpc(
      'get_solde_tresorerie_reel',
      { p_entreprise_id: entrepriseId }
    );

    if (soldeError) throw soldeError;
    const soldeRow = Array.isArray(soldeData) ? soldeData[0] : soldeData;
    soldeActuel = soldeRow?.solde_ttc ?? soldeRow ?? 0;
    soldeActuelHt = soldeRow?.solde_ht ?? Math.round((soldeActuel as number) / 1.2);
    soldeSource = 'calcule';
  }

  // 2. Récupérer les encaissements prévus
  const { data: encaissementsData, error: encError } = await supabase.rpc(
    'get_encaissements_prevus',
    {
      p_entreprise_id: entrepriseId,
      p_date_debut: dateDebutStr,
      p_date_fin: dateFinStr,
    }
  );

  if (encError) throw encError;

  // 3. Récupérer les décaissements prévus
  const { data: decaissementsData, error: decError } = await supabase.rpc(
    'get_decaissements_prevus',
    {
      p_entreprise_id: entrepriseId,
      p_date_debut: dateDebutStr,
      p_date_fin: dateFinStr,
    }
  );

  if (decError) throw decError;

  // 4. Récupérer les salaires prévus
  const { data: salairesData, error: salError } = await supabase.rpc(
    'get_salaires_prevus_par_mois_v2',
    {
      p_entreprise_id: entrepriseId,
      p_date_debut: dateDebutStr,
      p_date_fin: dateFinStr,
    }
  );

  if (salError) throw salError;

  // 5. Récupérer les charges fixes actives
  const { data: chargesFixesData, error: cfError } = await supabase
    .from('charges_fixes')
    .select('*')
    .eq('entreprise_id', entrepriseId)
    .eq('actif', true);

  if (cfError) throw cfError;
  const chargesFixes = (chargesFixesData as ChargeFix[]) || [];

  // 6. Construire la structure par mois
  const moisList: MoisTresorerie[] = [];
  let soldeRunning = soldeActuel;
  let soldeRunningHt = soldeActuelHt;
  const currentMoisKey = format(new Date(), 'yyyy-MM');

  for (let i = 0; i < 3; i++) {
    const moisDate = addMonths(dateDebut, i);
    const moisKey = format(moisDate, 'yyyy-MM');
    const moisLabel = format(moisDate, 'MMMM yyyy', { locale: fr });
    const moisLabelCapitalized = moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1);

    // Filtrer les encaissements pour ce mois
    const encaissementsMois: FluxTresorerie[] = ((encaissementsData as FluxSQL[]) || [])
      .filter((e) => e.date_prevue?.startsWith(moisKey))
      .map((e) => ({
        id: e.id,
        type: 'encaissement' as const,
        categorie: e.categorie as CategorieEncaissement,
        libelle: e.libelle,
        montant: Math.abs(Number(e.montant)),
        montant_ht: Math.abs(Number(e.montant_ht ?? e.montant)),
        date_prevue: e.date_prevue,
        statut: e.statut as 'prevu' | 'en_retard' | 'realise',
        reference_id: e.reference_id || undefined,
        reference_table: e.reference_table || undefined,
        chantier_id: e.chantier_id || undefined,
        chantier_nom: e.chantier_nom || undefined,
      }));

    // Filtrer les décaissements pour ce mois
    const decaissementsMois: FluxTresorerie[] = ((decaissementsData as FluxSQL[]) || [])
      .filter((d) => d.date_prevue?.startsWith(moisKey))
      .map((d) => ({
        id: d.id,
        type: 'decaissement' as const,
        categorie: d.categorie as CategorieDecaissement,
        libelle: d.libelle,
        montant: Math.abs(Number(d.montant)),
        montant_ht: Math.abs(Number(d.montant_ht ?? d.montant)),
        date_prevue: d.date_prevue,
        statut: d.statut as 'prevu' | 'en_retard' | 'realise',
        reference_id: d.reference_id || undefined,
        reference_table: d.reference_table || undefined,
        chantier_id: d.chantier_id || undefined,
        chantier_nom: d.chantier_nom || undefined,
      }));

    // Ajouter les salaires du mois
    const salaireMois = (salairesData as SalairesMoisSQL[] | null)?.find(
      (s) => format(new Date(s.mois), 'yyyy-MM') === moisKey
    );

    if (salaireMois && salaireMois.montant_salaires > 0) {
      decaissementsMois.push({
        id: `salaires-${moisKey}`,
        type: 'decaissement',
        categorie: 'salaires',
        libelle: `Salaires équipe (${salaireMois.nb_membres} membres)`,
        montant: Number(salaireMois.montant_salaires),
        montant_ht: Number(salaireMois.montant_salaires),
        date_prevue: format(endOfMonth(moisDate), 'yyyy-MM-dd'),
        statut: 'prevu',
        reference_id: undefined,
        reference_table: undefined,
        chantier_id: undefined,
        chantier_nom: undefined,
      });
    }

    // Ajouter les charges fixes applicables à ce mois
    const chargesFixesMois = chargesFixes.filter(cf => {
      if (!chargeApplicableCeMois(cf, moisKey)) return false;

      // Anti-doublon : mois courant + mode calculé uniquement
      if (moisKey === currentMoisKey && soldeSource === 'calcule') {
        const [annee, moisNum] = moisKey.split('-').map(Number);
        const dernierJour = new Date(annee, moisNum, 0).getDate();
        const jourEffectif = Math.min(cf.jour_prelevement, dernierJour);
        const datePrelevement = new Date(annee, moisNum - 1, jourEffectif);
        const createdAt = new Date(cf.created_at);
        const createdAtDate = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
        const aujourdhui = new Date();
        const aujourdhuiDate = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate());

        if (datePrelevement >= createdAtDate && datePrelevement <= aujourdhuiDate) {
          return false;
        }
      }

      return true;
    });
    chargesFixesMois.forEach(cf => {
      decaissementsMois.push({
        id: `charge-fixe-${cf.id}-${moisKey}`,
        type: 'decaissement',
        categorie: 'charges_fixes',
        libelle: cf.nom,
        montant: cf.montant_ttc,
        montant_ht: cf.montant_ht,
        date_prevue: `${moisKey}-${String(cf.jour_prelevement).padStart(2, '0')}`,
        statut: 'prevu',
        reference_id: cf.id,
        reference_table: 'charges_fixes',
        chantier_id: undefined,
        chantier_nom: undefined,
      });
    });

    // Calculer les totaux TTC
    const totalEncaissements = encaissementsMois.reduce((sum, e) => sum + e.montant, 0);
    const totalDecaissements = decaissementsMois.reduce((sum, d) => sum + d.montant, 0);
    const soldeFin = soldeRunning + totalEncaissements - totalDecaissements;

    // Calculer les totaux HT
    const totalEncaissementsHt = encaissementsMois.reduce((sum, e) => sum + e.montant_ht, 0);
    const totalDecaissementsHt = decaissementsMois.reduce((sum, d) => sum + d.montant_ht, 0);
    const soldeFinHt = soldeRunningHt + totalEncaissementsHt - totalDecaissementsHt;

    // Calculer le détail par catégorie (TTC)
    const detailEncaissements: Partial<Record<CategorieEncaissement, number>> = {};
    encaissementsMois.forEach((e) => {
      const cat = e.categorie as CategorieEncaissement;
      detailEncaissements[cat] = (detailEncaissements[cat] || 0) + e.montant;
    });

    const detailDecaissements: Partial<Record<CategorieDecaissement, number>> = {};
    decaissementsMois.forEach((d) => {
      const cat = d.categorie as CategorieDecaissement;
      detailDecaissements[cat] = (detailDecaissements[cat] || 0) + d.montant;
    });

    // Calculer le détail par catégorie (HT)
    const detailEncaissementsHt: Partial<Record<CategorieEncaissement, number>> = {};
    encaissementsMois.forEach((e) => {
      const cat = e.categorie as CategorieEncaissement;
      detailEncaissementsHt[cat] = (detailEncaissementsHt[cat] || 0) + e.montant_ht;
    });

    const detailDecaissementsHt: Partial<Record<CategorieDecaissement, number>> = {};
    decaissementsMois.forEach((d) => {
      const cat = d.categorie as CategorieDecaissement;
      detailDecaissementsHt[cat] = (detailDecaissementsHt[cat] || 0) + d.montant_ht;
    });

    moisList.push({
      mois: moisKey,
      label: moisLabelCapitalized,
      solde_debut: soldeRunning,
      encaissements: encaissementsMois,
      decaissements: decaissementsMois,
      total_encaissements: totalEncaissements,
      total_decaissements: totalDecaissements,
      solde_fin: soldeFin,
      detail_encaissements: detailEncaissements,
      detail_decaissements: detailDecaissements,
      // Versions HT
      solde_debut_ht: soldeRunningHt,
      total_encaissements_ht: totalEncaissementsHt,
      total_decaissements_ht: totalDecaissementsHt,
      solde_fin_ht: soldeFinHt,
      detail_encaissements_ht: detailEncaissementsHt,
      detail_decaissements_ht: detailDecaissementsHt,
    });

    soldeRunning = soldeFin;
    soldeRunningHt = soldeFinHt;
  }

  // Identifier les mois en déficit
  const moisEnDeficit = moisList
    .filter((m) => m.solde_fin < 0)
    .map((m) => m.mois);

  // Construire la prévision complète
  const prevision: PrevisionTresorerie = {
    solde_actuel: soldeActuel,
    solde_actuel_source: soldeSource,
    date_calcul: new Date().toISOString(),
    mois: moisList,
    total_encaissements_3mois: moisList.reduce((sum, m) => sum + m.total_encaissements, 0),
    total_decaissements_3mois: moisList.reduce((sum, m) => sum + m.total_decaissements, 0),
    solde_fin_periode: moisList[moisList.length - 1]?.solde_fin ?? soldeActuel,
    // Versions HT
    solde_actuel_ht: soldeActuelHt,
    total_encaissements_3mois_ht: moisList.reduce((sum, m) => sum + m.total_encaissements_ht, 0),
    total_decaissements_3mois_ht: moisList.reduce((sum, m) => sum + m.total_decaissements_ht, 0),
    solde_fin_periode_ht: moisList[moisList.length - 1]?.solde_fin_ht ?? soldeActuelHt,
    mois_en_deficit: moisEnDeficit,
  };

  return { prevision, params };
}

// =============================================================================
// Hook useTresorerie
// =============================================================================

export function useTresorerie(entrepriseId: string | null): UseTresorerieReturn {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const enabled = !!entrepriseId;

  // ============================================================================
  // QUERY
  // ============================================================================

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.tresorerie.prevision(entrepriseId!),
    queryFn: () => loadPrevisionData(entrepriseId!),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 min (données financières)
  });

  // ============================================================================
  // MUTATION : Mettre à jour le solde manuel
  // ============================================================================

  const updateSoldeMutation = useMutation({
    mutationFn: async (montant: number | null) => {
      const newParams: ParametresTresorerie = {
        solde_initial_manuel: montant,
        utiliser_solde_manuel: montant !== null,
      };

      // Si l'utilisateur définit un solde manuel, marquer la trésorerie comme initialisée (latch: jamais remis à false)
      const updateData: Record<string, unknown> = { params_tresorerie: newParams };
      if (montant !== null) {
        updateData.tresorerie_initialisee = true;
      }

      const { error: updateError } = await supabase
        .from('entreprises')
        .update(updateData)
        .eq('id', entrepriseId!);

      if (updateError) throw updateError;
      return montant;
    },
    onSuccess: (montant) => {
      toast({
        title: 'Paramètres enregistrés',
        description: montant !== null
          ? `Solde initial défini à ${montant.toLocaleString('fr-FR')} €`
          : 'Solde calculé automatiquement',
      });
      invalidateTresorerie(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('Erreur mise à jour solde manuel:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'tresorerie_update_solde',
        entrepriseId,
      });
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les paramètres.',
        variant: 'destructive',
      });
    },
  });

  // ============================================================================
  // WRAPPERS
  // ============================================================================

  const updateSoldeManuel = useCallback(
    async (montant: number | null) => {
      if (!entrepriseId) return;
      await updateSoldeMutation.mutateAsync(montant);
    },
    [entrepriseId, updateSoldeMutation]
  );

  // Convertir l'erreur React Query en string pour garder l'API identique
  const errorMsg = error
    ? ((error as any)?.message || 'Erreur lors du chargement de la trésorerie')
    : null;

  // Toast d'erreur sur le chargement initial (comme avant)
  // Note: React Query retry 3 fois avant d'échouer, donc le toast n'apparait qu'en cas d'erreur persistante

  return {
    prevision: data?.prevision ?? null,
    params: data?.params ?? null,
    isLoading,
    error: errorMsg,
    updateSoldeManuel,
    refetch: async () => { await refetch(); },
  };
}
